import React from 'react';
import {observable, action, computed, makeObservable, runInAction} from 'mobx';
import {observer} from 'mobx-react';
import store from '../../s';
import {checkout} from '../../../api';
import Message from '../msg';
import ModalLineCustom from '../modals/line-custom';
import ModalLineEP from '../modals/line-ep';
import ModalLineGradYear from '../modals/line-grad-year';
import Coord from './coord';
import ModalSimple from '../../../global/modal-simple';
import AbTestStore, {ABTestKeys} from '../../../global/store/ab-test.store';
import * as emitter from '../../../global/emitter';
import ButtonMultilineShipments from './components/button-multiline-shipments/button-multiline-shipments';
import {currency, isNullOrWhiteSpace, Portal} from '../../../util';

declare var modalOpen: any;

@observer
class CartItemList extends React.Component {
    @observable lastEditedId: number;
    @observable modalImageURL: string;
    @observable modalTitle: string;
    @observable loading: boolean;
    @observable uniqueId: number;
    @observable showQtyInput: boolean;
    @observable debounceTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(props) {
        super(props);
        this.changeQty = this.changeQty.bind(this);
        this.removeProduct = this.removeProduct.bind(this);
        this.doModal = this.doModal.bind(this);
        this.loading = false;
        this.uniqueId = Math.floor(Math.random() * 100000001);
        this.getQtyInputVisibility();
        this.buyNowClicked();
        this.skipStepsABTest();
        makeObservable(this);
    }

    abForceUpdate = async () => this.forceUpdate();

    componentDidMount() {
        emitter.eventEmitter.subscribe(emitter.EventType.AB, 'ab', this.abForceUpdate);
    }

    componentWillUnmount() {
        emitter.eventEmitter.unsubscribe(emitter.EventType.AB, 'ab');
    }

    @computed get cartItemTotal() {
        return store.checkout.cart.items.map(o => o.qty).reduce((a, b) => a + b, 0);
    }

    @action
    skipStepsABTest = async () => {
        if (store.checkoutABSkip === 2 && store.checkout.cart.items?.length && !store.performedSkip) {
            store.performedSkip = true;
            store.moveStep(ICheckoutStep.oci);

            store.uiCheckout.isLoadingEstimated = true;
            await checkout('get-est');

            runInAction(() => {
                // approved ria
                store.uiCheckout.isLoadingEstimated = false;
            });
        }
    };

    @action
    getQtyInputVisibility = () => {
        this.showQtyInput = store.checkout.cart.items.some(i => i.qty > 10);
    };

    @action
    changeQty = async (e: React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>, id: number, forceUpdate: boolean = false) => {
        const target = e.target as HTMLInputElement;
        let newQty = parseInt(target.value, 10);
        const item = store.checkout.cart.items.filter(o => o.id === id)[0];

        this.lastEditedId = id;
        const oldQty = item.qty;

        if (newQty === -1) {
            this.showQtyInput = true;
            newQty = 10;
        } else if (newQty === 0 || isNaN(newQty)) {
            return;
        }

        item.qty = newQty;

        const change = async () => {
            this.loading = true;
            const success = await checkout('update-qty');

            if (!success) {
                item.qty = oldQty;
            }

            this.cartStatusChanged(item.qty);
            this.loading = false;
        };

        const setDebounceTime = (this.showQtyInput || item.qty > 10) && +target.value !== -1;

        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }

        if (forceUpdate || !setDebounceTime) {
            await change();
        } else {
            this.debounceTimeout = setTimeout(async () => {
                await change();
            }, 2000);
        }
    };

    @action
    removeProduct = async (id: number) => {
        this.loading = true;
        let newQty = 0;
        const item = store.checkout.cart.items.filter(o => o.id === id)[0];
        this.lastEditedId = id;
        const oldQty = item.qty;
        // console.log('newQty', newQty);
        if (newQty === -1) {
            this.showQtyInput = true;
            newQty = 11;
        }
        item.qty = newQty;
        const success = await checkout('update-qty');
        if (!success) {
            item.qty = oldQty;
        }

        this.cartStatusChanged();
        this.loading = false;
    };

    cartStatusChanged(quantity: number = 0) {
        const element = document.getElementById('cart-status-changed');

        if (!element) {
            return;
        }

        if (quantity) {
            element.textContent = `Product quantity has been changed to ${quantity}`;
        } else {
            element.textContent = 'Product has been removed from the cart';
        }
    }

    @action
    doModal(o: ICartLineItem) {
        this.modalImageURL = o.imgFull;
        this.modalTitle = `${o.title1} ${o.title2}`;
        modalOpen('modal-cart-image-main');
    }

    @action
    buyNowClicked() {
        const param = new URLSearchParams(window.location.search).get('buyNow');

        if (param && +param === 1) {
            store.moveStep(ICheckoutStep.oci);
        }
    }

    render() {
        return (
            <div>
                <div
                    className="checkout-header"
                    style={{color: Portal?.PortalID === 3 ? '#33393a' : '', backgroundColor: Portal?.PortalID === 3 ? '#edece9' : ''}}
                >
                    <span className="d-none d-md-block">
                        Your Cart: {this.cartItemTotal.toString()} item
                        {this.cartItemTotal > 1 ? 's' : ''}
                    </span>
                </div>
                <div className={store.checkout.cstep <= ICheckoutStep.checkout ? 'd-none d-md-block cart-desktop' : 'cart-desktop'}>
                    {store.checkout.cart.items.map((o, _p) => (
                        <div
                            className="row item-detail-row"
                            key={_p}
                        >
                            <div className="col-9">
                                <div className="cd-item">
                                    <div className="img-space">
                                        <img
                                            height={400}
                                            width={400}
                                            tabIndex={0}
                                            src={o.imgThumb.replace('_200_200.png', '_400_400.png').replace('images.d', 'images2.d').replace('com//', 'com/')}
                                            alt={`${o.title1} ${o.title2}`}
                                            className={`resp float-left p-overlay-master${!store.checkout.isMyFrames && o.is_fast_shipping ? ' border-fast-shipping' : ''}`}
                                            loading="lazy"
                                            onClick={() => this.doModal(o)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    this.doModal(o);
                                                }
                                            }}
                                        />
                                        {!store.checkout.isMyFrames && o.is_fast_shipping && o.productId !== 24715 && o.productId !== 24716 && o.productId !== 24717 && o.productId !== 67336 && o.productId !== 67337 && (
                                            <div className={`fast-shipping fast-shipping-${Portal?.PortalID}`}>
                                                FAST SHIPPING <i className={Portal?.PortalID !== 3 ? `fas fa-shipping-fast` : `fas fa-cart-shopping-fast`}></i>
                                            </div>
                                        )}
                                    </div>
                                    <div className="non-image-space">
                                        <div className="title1 b">
                                            <a
                                                href={o.URL}
                                                aria-label={`View Cart Product ${o.title1}`}
                                            >
                                                {o.title1}
                                            </a>
                                        </div>
                                        <div className="title2">
                                            <a
                                                href={o.URL}
                                                aria-label={`View Cart Product ${o.title2}`}
                                            >
                                                {o.title2}
                                            </a>
                                        </div>
                                        <div className="priceDetail">
                                            {o.qty > 1 ? `Qty: ${o.qty} @` : ''} {currency(o.price)}
                                        </div>
                                        {o.qty > 1 && <div className="price">{currency(o.price * o.qty)}</div>}
                                        {/* <div className='more' data-toggle='collapse' data-target={`#details-cd-${o.id.toString()}`}>details</div> */}
                                        <div
                                            id={`details-cd-${o.id.toString()}`}
                                            className="pad-top"
                                            style={{paddingLeft: 6}}
                                        >
                                            <div className="details">
                                                {o.details.map((d, i) => (
                                                    <div
                                                        key={i}
                                                        dangerouslySetInnerHTML={{__html: d}}
                                                    />
                                                ))}
                                                {o.hasEp && (
                                                    <div>
                                                        <div>
                                                            <ModalLineEP
                                                                id={o.id}
                                                                modalId={`modal-line-ep-${o.id}`}
                                                            />
                                                            <span className="details-title">{o.epTitle}</span>
                                                            <span
                                                                style={{
                                                                    paddingLeft: 4,
                                                                    fontSize: 12,
                                                                }}
                                                                className="link"
                                                                onClick={() => modalOpen(`modal-line-ep-${o.id}`)}
                                                            >
                                                                <i className="fas fa-edit link pointer"></i>
                                                            </span>
                                                        </div>
                                                        <div style={{paddingLeft: 6}}>
                                                            {o.ep1 && <div>{o.ep1}</div>}
                                                            {o.ep2 && <div>{o.ep2}</div>}
                                                            {o.ep3 && <div>{o.ep3}</div>}
                                                            {o.ep4 && <div>{o.ep4}</div>}
                                                            {o.ep5 && <div>{o.ep5}</div>}
                                                            {o.ep6 && <div>{o.ep6}</div>}
                                                            {o.ep7 && <div>{o.ep7}</div>}
                                                            {o.ep8 && <div>{o.ep8}</div>}
                                                            {o.ep9 && <div>{o.ep9}</div>}
                                                            {o.ep10 && <div>{o.ep10}</div>}
                                                        </div>
                                                    </div>
                                                )}

                                                {o.hasCustom && (
                                                    <div>
                                                        <div>
                                                            <ModalLineCustom
                                                                id={o.id}
                                                                modalId={`modal-line-custom-${o.id}`}
                                                            />
                                                            {((o.CustomField1_Title.length > 0 && o.CustomField1_Title.trim().length > 0 && o.CustomField1.length > 0) || o.productId === 88937) && (
                                                                <div>
                                                                    {o.productId !== 88937 && (
                                                                        <span
                                                                            className="details-title"
                                                                            style={{
                                                                                color: '#616161',
                                                                            }}
                                                                        >
                                                                            {o.CustomField1_Title}:
                                                                        </span>
                                                                    )}{' '}
                                                                    {o.CustomField1}
                                                                    <button
                                                                        style={{
                                                                            paddingLeft: 4,
                                                                            fontSize: 12,
                                                                        }}
                                                                        className="link text-btn"
                                                                        type="button"
                                                                        aria-label={`Edit ${o.CustomField1_Title}`}
                                                                        onClick={() => modalOpen(`modal-line-custom-${o.id}`)}
                                                                    >
                                                                        <i className="fas fa-edit link pointer"></i>
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {o.CustomField2_Title.length > 0 && o.CustomField2_Title.trim().length > 0 && o.CustomField2.length > 0 && (
                                                                <div>
                                                                    <span
                                                                        className="details-title"
                                                                        style={{
                                                                            color: '#616161',
                                                                        }}
                                                                    >
                                                                        {o.CustomField2_Title}:
                                                                    </span>{' '}
                                                                    {o.CustomField2}
                                                                    <button
                                                                        style={{
                                                                            paddingLeft: 4,
                                                                            fontSize: 12,
                                                                        }}
                                                                        className="link text-btn"
                                                                        type="button"
                                                                        aria-label={`Edit ${o.CustomField2_Title}`}
                                                                        onClick={() => modalOpen(`modal-line-custom-${o.id}`)}
                                                                    >
                                                                        <i className="fas fa-edit link pointer"></i>
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {o.CustomField3_Title.length > 0 && o.CustomField3_Title.trim().length > 1 && o.CustomField3.length > 0 && (
                                                                <div>
                                                                    <span
                                                                        className="details-title"
                                                                        style={{
                                                                            color: '#616161',
                                                                        }}
                                                                    >
                                                                        {o.CustomField3_Title}:
                                                                    </span>{' '}
                                                                    {o.CustomField3}
                                                                    <button
                                                                        style={{
                                                                            paddingLeft: 4,
                                                                            fontSize: 12,
                                                                        }}
                                                                        className="link text-btn"
                                                                        type="button"
                                                                        aria-label={`Edit ${o.CustomField3_Title}`}
                                                                        onClick={() => modalOpen(`modal-line-custom-${o.id}`)}
                                                                    >
                                                                        <i className="fas fa-edit link pointer"></i>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {!o.IsDYF && o.gradYear > 0 && (
                                                    <div>
                                                        <div>
                                                            <ModalLineGradYear
                                                                id={o.id}
                                                                modalId={`modal-line-grad-year-${o.id}-${this.uniqueId}`}
                                                            />
                                                            <span
                                                                className="details-title"
                                                                style={{color: '#616161'}}
                                                            >
                                                                Grad Year:
                                                            </span>{' '}
                                                            {o.gradYear.toString()}
                                                            <button
                                                                style={{
                                                                    paddingLeft: 4,
                                                                    fontSize: 12,
                                                                }}
                                                                type="button"
                                                                aria-label="Edit Year of Graduation"
                                                                className="link text-btn"
                                                                onClick={() => modalOpen(`modal-line-grad-year-${o.id}-${this.uniqueId}`)}
                                                            >
                                                                <i className="fas fa-edit link pointer"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {o.discountHighlight !== '' && (
                                                    <div
                                                        className="discount-highlight"
                                                        dangerouslySetInnerHTML={{
                                                            __html: o.discountHighlight,
                                                        }}
                                                    />
                                                )}
                                                {!isNullOrWhiteSpace(o.special_msg) && (
                                                    <div
                                                        className="discount-highlight"
                                                        style={{textAlign: 'left'}}
                                                    >
                                                        {o.special_msg}
                                                    </div>
                                                )}

                                                {o.messages
                                                    .filter(y => y.msgType === ICheckoutMessageType.backorder)
                                                    .map(x => (
                                                        <div className="alert alert-info">{x.msg}</div>
                                                    ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {!this.loading && (
                                <div className="col-3 text-right">
                                    <div className="cart-desktop-price">{currency(o.price)}</div>
                                    {o.VolumeDiscountAmt > 0 && (
                                        <div className="volume">
                                            Saving <b>{currency(o.VolumeDiscountAmt)}</b> with {o.VolumeDiscountPercent}% volume discount
                                        </div>
                                    )}
                                    {o.productId !== 24791 && (
                                        <div
                                            className="form-inline"
                                            style={{justifyContent: 'flex-end'}}
                                        >
                                            {!this.showQtyInput && o.qty <= 10 && (
                                                <select
                                                    className="form-select change-qty"
                                                    aria-label="Change Quantity"
                                                    value={o.qty}
                                                    name={`qty-${_p.toString()}`}
                                                    onChange={e => this.changeQty(e, o.id)}
                                                >
                                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, -1].map((q, _i) => (
                                                        <option
                                                            key={_i}
                                                            value={q}
                                                        >
                                                            {q === -1 ? 'custom' : q.toString()}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                            {(this.showQtyInput || o.qty > 10) && (
                                                <input
                                                    className="form-control change-qty"
                                                    aria-label="Change Quantity"
                                                    value={o.qty}
                                                    name={`qty-tb-${_p.toString()}`}
                                                    onChange={e => this.changeQty(e, o.id, false)}
                                                    onBlur={e => {
                                                        if (+e.target.value !== o.qty) {
                                                            this.changeQty(e, o.id, true);
                                                        }
                                                    }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            this.changeQty(e, o.id, true);
                                                        }
                                                    }}
                                                />
                                            )}
                                            {this.lastEditedId === o.id && (
                                                <div>
                                                    <Message msgType={ICheckoutMessageType.cart_qty} />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <button
                                        className="remove text-btn"
                                        type="button"
                                        onClick={() => this.removeProduct(o.id)}
                                        aria-label="Remove product from cart"
                                    >
                                        Remove
                                    </button>

                                    {o.storeId === 205164 && <ButtonMultilineShipments cartItem={o} />}

                                    {Portal?.PortalID !== 3 && (
                                        <div
                                            className="remove"
                                            style={{paddingTop: '24px'}}
                                        >
                                            <a
                                                href={o.URL}
                                                className="btn-checkout btn-checkout-white"
                                                aria-label="Edit Product"
                                                style={{
                                                    fontSize: '15px',
                                                    fontWeight: 400,
                                                    color: '#171717',
                                                }}
                                            >
                                                {o.MouldingID > 0 ? 'Edit Frame' : 'Edit'}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}
                            {this.loading && (
                                <div className="col-3 text-right">
                                    <div className="cart-desktop-price">{currency(o.price)}</div>
                                    <div
                                        className="form-inline"
                                        style={{paddingRight: '12%', paddingTop: '4%'}}
                                    >
                                        <i className="fas fa-circle-notch fa-spin fa-2x" />
                                    </div>
                                </div>
                            )}
                            {store.checkout.cart.items.length > 1 && (
                                <div className="col-12">
                                    <hr />
                                </div>
                            )}
                            <div
                                id="cart-status-changed"
                                className="sr-only"
                                aria-live="polite"
                            ></div>
                        </div>
                    ))}

                    {store.checkout.cart.items && store.checkout.cart.items.length > 0 && !AbTestStore.ABIsVariant(ABTestKeys.ab_checkout_1, 1) && (
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                fontSize: 14,
                                marginTop: 16,
                            }}
                        >
                            <div>
                                <picture>
                                    <source
                                        type="image/webp"
                                        srcSet={`https://${Portal?.CDNDomain}/images/cached/general/ppa25.webp`}
                                    />
                                    <img
                                        width="94"
                                        height="26"
                                        loading="lazy"
                                        src={`https://${Portal?.CDNDomain}/images/cached/general/ppa25.jpg`}
                                        alt="PayPal Pay Later"
                                        style={{height: 16, width: 'auto'}}
                                    />
                                </picture>
                            </div>

                            <div style={{fontSize: 14, paddingLeft: 6, marginTop: 2}}>
                                Pay in 4 payments of {currency(store.checkout.cart.pricing.total / 4)}.{' '}
                                <a
                                    href="https://www.paypal.com/us/digital-wallet/ways-to-pay/buy-now-pay-later"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link"
                                    style={{fontSize: 14, textDecoration: 'underline'}}
                                >
                                    Learn More.
                                </a>
                            </div>
                        </div>
                    )}

                    {(!store.checkout.cart.items || store.checkout.cart.items.length < 2) && <div className="hr"></div>}

                    {store.checkout.cart.items && store.checkout.cart.items.length > 0 && store.checkout.cstep === ICheckoutStep.cart && (
                        <div className="continue-shopping">
                            <a
                                href={Portal && Portal.PortalID < 3 ? store.checkout.cart.items[store.checkout.cart.items.length - 1].StoreURL : '/'}
                                className="underline"
                                aria-label="Continue Shopping"
                            >
                                Continue Shopping {store.checkout.cart.items.length > 0 && store.checkout.cart.items[0].title1}
                            </a>
                        </div>
                    )}

                    {(!store.checkout.cart.items || store.checkout.cart.items.length < 1) && (
                        <div className="text-center">
                            <div
                                className="form-inline-label"
                                style={{fontSize: 28}}
                            >
                                Your cart is empty.
                            </div>
                            {Portal?.PortalID === 3 && (
                                <div
                                    className="link"
                                    style={{marginTop: 36}}
                                >
                                    <a
                                        href="/"
                                        title="Diploma Frames"
                                    >
                                        Visit our Framerly Shop
                                    </a>
                                </div>
                            )}
                            {Portal?.PortalID === 0 && (
                                <div
                                    className="link"
                                    style={{marginTop: 36}}
                                >
                                    <a
                                        href="/shop-frames-gifts.aspx"
                                        title="Diploma Frames"
                                    >
                                        Visit our Shop Frames and Gifts page
                                    </a>
                                </div>
                            )}
                            {Portal?.PortalID === 0 && (
                                <div
                                    className="link"
                                    style={{marginTop: 36}}
                                >
                                    <a
                                        href="/frame-designer"
                                        title="Custom Frame"
                                    >
                                        Create your own Custom Frame
                                    </a>
                                </div>
                            )}
                            {Portal?.PortalID === 0 && (
                                <div
                                    className="link"
                                    style={{marginTop: 36}}
                                >
                                    <a
                                        href="/about-our-frames/frames-custom-made-for-you.aspx"
                                        title="About Our Frames"
                                    >
                                        Learn more about our frames
                                    </a>
                                </div>
                            )}
                        </div>
                    )}

                    {Portal && Portal.PortalID < 1 && <Coord />}
                </div>

                <ModalSimple
                    id="modal-cart-image-main"
                    title={`${this.modalTitle}`}
                >
                    <div>
                        <img
                            src={this.modalImageURL}
                            alt={`${this.modalTitle}`}
                            className="resp"
                        />
                    </div>
                </ModalSimple>
            </div>
        );
    }
}

export default CartItemList;
